import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { Icon } from "../../lib/icons";
import { Button } from "../Button";
import { IconButton } from "../IconButton";
import { Link } from "../Link";
import { cn } from "../../lib/utils";

const alertVariants = cva(
  "relative flex gap-4 p-4 rounded-large transition-colors",
  {
    variants: {
      emphasis: {
        subtle: "",
        intense: "",
      },
      intent: {
        positive: "",
        negative: "",
        notice: "",
        info: "",
        neutral: "",
      },
    },
    compoundVariants: [
      // Subtle emphasis
      {
        emphasis: "subtle",
        intent: "positive",
        class: "bg-feedback-fill-positive-subtle",
      },
      {
        emphasis: "subtle",
        intent: "negative",
        class: "bg-feedback-fill-negative-subtle",
      },
      {
        emphasis: "subtle",
        intent: "notice",
        class: "bg-feedback-fill-notice-subtle",
      },
      {
        emphasis: "subtle",
        intent: "info",
        class: "bg-feedback-fill-info-subtle",
      },
      {
        emphasis: "subtle",
        intent: "neutral",
        class: "bg-feedback-fill-neutral-subtle",
      },
      // Intense emphasis
      {
        emphasis: "intense",
        intent: "positive",
        class: "bg-feedback-fill-positive-intense",
      },
      {
        emphasis: "intense",
        intent: "negative",
        class: "bg-feedback-fill-negative-intense",
      },
      {
        emphasis: "intense",
        intent: "notice",
        class: "bg-feedback-fill-notice-intense",
      },
      {
        emphasis: "intense",
        intent: "info",
        class: "bg-feedback-fill-info-intense",
      },
      {
        emphasis: "intense",
        intent: "neutral",
        class: "bg-feedback-fill-neutral-intense",
      },
    ],
    defaultVariants: {
      emphasis: "subtle",
      intent: "info",
    },
  }
);

const alertIconVariants = cva(
  "flex-shrink-0 rounded-full flex items-center justify-center mt-1",
  {
    variants: {
      emphasis: {
        subtle: "",
        intense: "text-white",
      },
      intent: {
        positive: "",
        negative: "",
        notice: "",
        info: "",
        neutral: "",
      },
    },
    compoundVariants: [
      // Subtle emphasis - colored backgrounds with white icons
      {
        emphasis: "subtle",
        intent: "positive",
        class: "text-action-ink-positive-normal",
      },
      {
        emphasis: "subtle",
        intent: "negative",
        class: " text-action-ink-negative-normal",
      },
      {
        emphasis: "subtle",
        intent: "notice",
        class: " text-action-ink-notice-normal",
      },
      {
        emphasis: "subtle",
        intent: "info",
        class: " text-action-ink-info-normal",
      },
      {
        emphasis: "subtle",
        intent: "neutral",
        class: " text-action-ink-neutral-normal",
      },
      // Intense emphasis - white backgrounds with colored icons
      {
        emphasis: "intense",
        intent: "positive",
        class: " text-action-ink-on-primary-normal",
      },
      {
        emphasis: "intense",
        intent: "negative",
        class: " text-action-ink-on-primary-normal",
      },
      {
        emphasis: "intense",
        intent: "notice",
        class: " text-action-ink-on-primary-normal",
      },
      {
        emphasis: "intense",
        intent: "info",
        class: " text-action-fill-info-default",
      },
      {
        emphasis: "intense",
        intent: "neutral",
        class: " text-action-fill-neutral-default",
      },
    ],
    defaultVariants: {
      emphasis: "subtle",
      intent: "info",
    },
  }
);

const alertTextVariants = cva("", {
  variants: {
    emphasis: {
      subtle: "",
      intense: "",
    },
    intent: {
      positive: "",
      negative: "",
      notice: "",
      info: "",
      neutral: "",
    },
  },
  compoundVariants: [
    // Subtle emphasis - dark text
    {
      emphasis: "subtle",
      intent: "positive",
      class: "text-surface-ink-neutral-normal",
    },
    {
      emphasis: "subtle",
      intent: "negative",
      class: "text-surface-ink-neutral-normal",
    },
    {
      emphasis: "subtle",
      intent: "notice",
      class: "text-surface-ink-neutral-normal",
    },
    {
      emphasis: "subtle",
      intent: "info",
      class: "text-surface-ink-neutral-normal",
    },
    {
      emphasis: "subtle",
      intent: "neutral",
      class: "text-surface-ink-neutral-normal",
    },
    // Intense emphasis - white text
    {
      emphasis: "intense",
      intent: "positive",
      class: "text-action-ink-on-primary-normal",
    },
    {
      emphasis: "intense",
      intent: "negative",
      class: "text-action-ink-on-primary-normal",
    },
    {
      emphasis: "intense",
      intent: "notice",
      class: "text-action-ink-on-primary-normal",
    },
    {
      emphasis: "intense",
      intent: "info",
      class: "text-action-ink-on-primary-normal",
    },
    {
      emphasis: "intense",
      intent: "neutral",
      class: "text-action-ink-on-primary-normal",
    },
  ],
  defaultVariants: {
    emphasis: "subtle",
    intent: "info",
  },
});

export interface AlertProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, "title"> {
  /**
   * The visual emphasis level of the alert
   */
  emphasis?: "subtle" | "intense";
  /**
   * The intent/type of the alert
   */
  intent?: "positive" | "negative" | "notice" | "info" | "neutral";
  /**
   * The title of the alert
   */
  title?: string;
  /**
   * The body content of the alert
   */
  children: React.ReactNode;
  /**
   * Whether the alert should take full width
   * When true, actions are placed to the right of the content
   * When false, actions are placed below the content
   */
  isFullWidth?: boolean;
  /**
   * Callback fired when the close button is clicked
   */
  onClose?: () => void;
  /**
   * Custom icon to display. If not provided, an icon will be automatically selected based on intent
   */
  icon?: React.ReactNode;
  /**
   * Action button text. If provided, a button will be automatically created based on the variant
   */
  actionButtonText?: string;
  /**
   * Callback fired when the action button is clicked
   */
  onActionButtonClick?: () => void;
  /**
   * Action link to display
   */
  actionLinkText?: string;
  /**
   * Callback fired when the action link is clicked
   */
  onActionLinkClick?: () => void;
  /**
   * Custom class name for the alert
   */
  className?: string;
}

const getDefaultIcon = (
  intent: AlertProps["intent"],
  emphasis: AlertProps["emphasis"]
) => {
  const iconSize = 16;
  const iconColor = emphasis === "intense" ? "currentColor" : "currentColor";

  switch (emphasis) {
    case "intense":
      return (
        <Icon
          name="info"
          size={iconSize}
          className="text-white"
          aria-hidden="true"
        />
      );
    default:
      return <Icon name="info" size={iconSize} aria-hidden="true" />;
  }
};

const getButtonColor = (
  intent: AlertProps["intent"]
): "primary" | "positive" | "negative" | "notice" | "info" | "neutral" => {
  switch (intent) {
    case "positive":
      return "positive";
    case "negative":
      return "negative";
    case "notice":
      return "notice";
    case "info":
      return "info";
    case "neutral":
      return "neutral";
    default:
      return "neutral";
  }
};

const getActionButton = (
  actionButtonText: string | undefined,
  onActionButtonClick: (() => void) | undefined,
  emphasis: AlertProps["emphasis"],
  intent: AlertProps["intent"]
): React.ReactNode | null => {
  if (!actionButtonText) return null;

  const buttonColor = getButtonColor(intent);

  // For intense emphasis: secondary variant with white border and white text
  // For subtle emphasis: secondary variant with colored border and colored text (default behavior)
  if (emphasis === "intense") {
    return (
      <Button
        variant="secondary"
        color="white"
        size="small"
        onClick={onActionButtonClick}
      >
        {actionButtonText}
      </Button>
    );
  }

  // Subtle emphasis: use default secondary variant styling
  return (
    <Button
      variant="secondary"
      color={
        intent as
          | "positive"
          | "negative"
          | "notice"
          | "info"
          | "neutral"
          | "primary"
      }
      size="small"
      onClick={onActionButtonClick}
    >
      {actionButtonText}
    </Button>
  );
};
const getActionLink = (
  actionButtonText: string | undefined,
  onActionLinkClick: (() => void) | undefined,
  emphasis: AlertProps["emphasis"],
  intent: AlertProps["intent"]
): React.ReactNode | null => {
  if (!actionButtonText) return null;

  const buttonColor = getButtonColor(intent);

  // For intense emphasis: secondary variant with white border and white text
  // For subtle emphasis: secondary variant with colored border and colored text (default behavior)
  if (emphasis === "intense") {
    return (
      <Link
        type="action"
        color="white"
        size="small"
        onClick={onActionLinkClick}
      >
        {actionButtonText}
      </Link>
    );
  }

  // Subtle emphasis: use default secondary variant styling
  return (
    <Link
      type="action"
      color={
        intent as
          | "positive"
          | "negative"
          | "notice"
          | "info"
          | "neutral"
          | "primary"
      }
      size="small"
      onClick={onActionLinkClick}
    >
      {actionButtonText}
    </Link>
  );
};

const Alert = React.forwardRef<HTMLDivElement, AlertProps>(
  (
    {
      className,
      emphasis = "subtle",
      intent = "info",
      title,
      children,
      isFullWidth = false,
      onClose,
      icon,
      actionButtonText,
      onActionButtonClick,
      actionLinkText,
      onActionLinkClick,
      ...props
    },
    ref
  ) => {
    const displayIcon = icon ?? getDefaultIcon(intent, emphasis);
    const actionButton = getActionButton(
      actionButtonText,
      onActionButtonClick,
      emphasis,
      intent
    );
    const actionLink = getActionLink(
      actionLinkText,
      onActionLinkClick,
      emphasis,
      intent
    );
    const hasActions = actionButton || actionLink;

    return (
      <div
        ref={ref}
        className={cn(alertVariants({ emphasis, intent }), className)}
        role="alert"
        {...props}
      >
        {/* Icon */}
        <div
          className={cn(
            alertIconVariants({ emphasis, intent }),
            "h-5 w-5 shrink-0"
          )}
        >
          {displayIcon}
        </div>

        {isFullWidth ? (
          /* Full Width Layout: Icon, Content, Actions/Close in one row */
          <div className="flex-1 min-w-0 flex items-center justify-between gap-4">
            {/* Content: Title and Body */}
            <div className="flex-1 min-w-0 flex flex-col gap-1">
              {title && (
                <div
                  className={cn(
                    "text-body-medium-semibold",
                    alertTextVariants({ emphasis, intent })
                  )}
                >
                  {title}
                </div>
              )}
              <div
                className={cn(
                  "text-body-small-regular",
                  alertTextVariants({ emphasis, intent })
                )}
              >
                {children}
              </div>
            </div>
            {/* Actions and Close - aligned to the right, vertically centered */}
            <div className="flex items-center gap-4 shrink-0">
              {hasActions && (
                <>
                  {actionButton}
                  {actionLink}
                </>
              )}
              {onClose && (
                <IconButton
                  icon="close"
                  color={
                    emphasis === "intense" ? "white" : getButtonColor(intent)
                  }
                  size="xsmall"
                  onClick={onClose}
                  className={cn(
                    "shrink-0 h-5 w-5",
                    emphasis === "intense"
                      ? "hover:bg-white/20"
                      : "hover:bg-black/10"
                  )}
                  aria-label="Close alert"
                />
              )}
            </div>
          </div>
        ) : (
          /* Not Full Width Layout: Icon and Content in row, Actions below, Close top-right */
          <div className="flex-1 min-w-0 relative">
            {/* Close button - positioned top right */}
            {onClose && (
              <div className="absolute top-0 right-0">
                <IconButton
                  icon="close"
                  color={
                    emphasis === "intense" ? "white" : getButtonColor(intent)
                  }
                  size="xsmall"
                  onClick={onClose}
                  className={cn(
                    "shrink-0 h-5 w-5",
                    emphasis === "intense"
                      ? "hover:bg-white/20"
                      : "hover:bg-black/10"
                  )}
                  aria-label="Close alert"
                />
              </div>
            )}

            {/* Content */}
            <div className="flex flex-col gap-3 pr-8">
              {title && (
                <div
                  className={cn(
                    "text-body-medium-semibold",
                    alertTextVariants({ emphasis, intent })
                  )}
                >
                  {title}
                </div>
              )}
              <div
                className={cn(
                  "text-body-small-regular",
                  alertTextVariants({ emphasis, intent })
                )}
              >
                {children}
              </div>

              {/* Actions - below body text, left-aligned */}
              {hasActions && (
                <div className="flex items-center gap-4">
                  {actionButton}
                  {actionLink}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  }
);

Alert.displayName = "Alert";

export { Alert, alertVariants };
